import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import QrScanner from "qr-scanner";

import {
  getKeylessSessionRAReview,
  validateKeylessVehicleQRCode,
} from "./keylessService";

import { DomainContext } from "../../domain-repository/DomainRepository";
import { isTestsEnv, isValidPlateNumber } from "../../utils/utils";
import { bigModalStyle, errorModalImgStyle, keylessColors } from "./utils";

import AlertModalManager from "../../components/alert-modal-wraper/alert-modal-manager";
import Loading from "../../components/Loading";
import { KeylessPageLayoutView } from "./KeylessPage";
import {
  KEYLESS_STEPS,
  KeylessPageLayout,
} from "./components/KeylessPageLayout";
import { KeylessButton } from "./components/KeylessButton";
import { KeylessModal } from "./components/KeylessModal";
import { QRCodeReader } from "../../components/qrcode-reader/QRCodeReader";

const vehicleSteps = {
  REVIEW: "REVIEW",
  PARKING_LOCATION: "PARKING_LOCATION",
  QR_CODE_EXPLAIN: "QR_CODE_EXPLAIN",
};

const isTests = isTestsEnv();

export function KeylessVehicleReview({
  handleNextStep,
  keylessToken,
  keylessSession,
}) {
  const { t } = useTranslation();
  const hasRun = useRef(false);
  const isValidating = useRef(false);

  const { localizationAssets } = useContext(DomainContext);

  const [isLoading, setIsLoading] = useState(false);
  const [raDetails, setRADetails] = useState(null);
  const [readingQrCode, setReadingQrCode] = useState(false);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [modalObj, setModalObj] = useState({
    isOpen: false,
  });
  const [manualPlate, setManualPlate] = useState('')

  const manualPlateValid = isValidPlateNumber(manualPlate)

  useEffect(() => {
    if (hasRun.current) return null;
    hasRun.current = true;
    setIsLoading(true);
    getKeylessSessionRAReview(keylessToken)
      .then((res) => {
        setRADetails(res);
        setSteps(
          res.parkingLocation
            ? [
                vehicleSteps.REVIEW,
                // vehicleSteps.PARKING_LOCATION,
                vehicleSteps.QR_CODE_EXPLAIN,
              ]
            : [vehicleSteps.REVIEW, vehicleSteps.QR_CODE_EXPLAIN]
        );
      })
      .catch((err) => {
        const messageKey = err.response.data;
        AlertModalManager.openModal(
          { title: t(messageKey), message: "" },
          () => {}
        );
      })
      .finally(() => setIsLoading(false));
  }, [t, keylessToken]);

  const handleLeaveClick = useCallback(() => {
    window.location.href = "/";
  }, []);

  const handleQRCodeRead = useCallback(
    (data) => {
      if (isValidating.current) return;
      isValidating.current = true;

      const qrcodeDataToSend = isTests
        ? `${keylessSession.unitNumber}|${keylessSession.plateNumber}`
        : data;

      setModalObj({
        isOpen: true,
        title: "keylessWaitTitle",
        message: "keylessLoadingValidateVehicleMessage",
        img: "keylessLoadingVehicle",
      });

      validateKeylessVehicleQRCode(keylessToken, qrcodeDataToSend)
        .then((res) => {
          setModalObj({
            isOpen: true,
            title: "keylessValidateVehicleSucessTitle",
            message: "",
            img: "keylessSuccess",
          });
          setTimeout(() => {
            handleNextStep(res.nextStep);
          }, 2500);
        })
        .catch((err) => {
          setIsLoading(false);
          setModalObj({
            isOpen: true,
            error: true,
            title: "keylessWarning",
            message: "keylessVehicleErrorTitle",
            img: "keylessError",
            actions: <KeylessButton onClick={handleLeaveClick} text={t("leave")} />
          });
          isValidating.current = false;
        });
    },
    [keylessToken]
  );

  if (isLoading || !raDetails) {
    return <Loading active={isLoading} />;
  }

  if (steps[currentStep] === vehicleSteps.REVIEW) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        localizationAssets={localizationAssets}
        reservationNumber={keylessSession.reservationNumber}
        footer={
          <KeylessButton
            onClick={() => setCurrentStep((i) => i + 1)}
            text={t("keylessNext")}
            color="white"
            backgroundColor={keylessColors.orange}
          />
        }
      >
        <div style={styles.pageContent} className="container">
          <h1 style={styles.title}>{t("keylessVehicleTitle")}</h1>
          <img
            style={styles.vehicleImage}
            src={raDetails.vehicleImage}
            alt=""
          />
          <div style={styles.plateNumber}>
            <span>{t("keylessPlateNumber")}</span>
            <span>{raDetails.plateNumber}</span>
          </div>
          <p style={styles.brand}>{raDetails.groupDetails.type}</p>
          <div style={styles.details}>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessFuelIcon"]}
                  alt=""
                />
              </div>
              <div>{t(raDetails.groupDetails.fuel)}</div>
            </div>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessTransmissionIcon"]}
                  alt=""
                />
              </div>
              <div>{t(raDetails.groupDetails.transmission)}</div>
            </div>
            <div style={styles.detailsItem}>
              <div style={styles.detailsItemImgWrapper}>
                <img
                  style={styles.detailsItemImg}
                  src={localizationAssets["keylessColourIcon"]}
                  alt=""
                />
              </div>
              <div>
                {raDetails.colour === "PADRAO"
                  ? null
                  : t(`vehicleColour-${raDetails.colour?.replace(/\s+/g, "")}`)}
              </div>
            </div>
          </div>
        </div>
      </KeylessPageLayout>
    );
  }

  if (steps[currentStep] === vehicleSteps.PARKING_LOCATION) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        localizationAssets={localizationAssets}
        handleBack={() => setCurrentStep((i) => i - 1)}
        reservationNumber={keylessSession.reservationNumber}
        footer={
          <KeylessButton
            onClick={() => setCurrentStep((i) => i + 1)}
            text={t("keylessNext")}
            color="white"
            backgroundColor={keylessColors.orange}
          />
        }
      >
        <div style={styles.pageContent} className="container">
          <h1 style={styles.title}>{t("keylessVehicleParkingTitle")}</h1>
          <img
            style={styles.parkingImage}
            src={localizationAssets["keylessParkingImage"]}
            alt=""
          />
          <div style={styles.parkingLocation}>
            <div>{t("keylessVehicleParkingLabel")}</div>
            <div style={styles.parkingLocationValue}>
              {raDetails.parkingLocation}
            </div>
          </div>
        </div>
      </KeylessPageLayout>
    );
  }

  if (steps[currentStep] === vehicleSteps.QR_CODE_EXPLAIN) {
    return (
      <KeylessPageLayout
        t={t}
        step={KEYLESS_STEPS.VEHICLE}
        localizationAssets={localizationAssets}
        handleBack={() => setCurrentStep((i) => i - 1)}
        reservationNumber={keylessSession.reservationNumber}
        footer={readingQrCode ? 
          <KeylessButton
            text={
              <div style={styles.qrcodeInputWrapper}>
                {t("keylessNext")}
              </div>
            }
            color="white"
            disabled={!manualPlateValid}
            backgroundColor={keylessColors.orange}
            onClick={() => manualPlateValid && handleQRCodeRead(`null|${manualPlate}`)}
          />
          :
          <KeylessButton
            text={
              <div style={styles.qrcodeInputWrapper}>
                {t("keylessReadQRCode")}
              </div>
            }
            color="white"
            backgroundColor={keylessColors.orange}
            onClick={() => setReadingQrCode(true)}
          />
        }
      >
        <div style={styles.pageContent} className="container">
          {readingQrCode ? 
            <div>
              <QRCodeReader handleSuccess={handleQRCodeRead} />
              <div className="mt-4">
                <input
                  type="text"
                  className={`form-control text-center`}
                  value={manualPlate}
                  onChange={(e) => setManualPlate(e.target.value)}
                />
              </div>
            </div>  
          : 
          <>
            <h1 style={styles.title}>{t("keylessVehicleQRcodeTitle")}</h1>
            <img
              style={styles.vehicleImage}
              src={localizationAssets["keylessVehicleQrCodeImage"]}
              alt=""
            />
            <div style={styles.filler} />
            <div style={styles.hint}>
              <img style={styles.hintIcon} src={localizationAssets["keylessVehicleQRcodeHint"]} alt="" />
              <p>{t("keylessVehicleQRcodeMessage")}</p>
            </div>
          </>
          }
        </div>
        <KeylessModal
          isOpen={modalObj.isOpen}
          title={t(modalObj.title)}
          message={t(modalObj.message)}
          img={localizationAssets[modalObj.img]}
          actions={modalObj.actions || <div>{t("keylessWaitMessage")}</div>}
          imgStyle={modalObj.error ? errorModalImgStyle : {}}
          modalStyle={modalObj.error ? {} : bigModalStyle}
        />
      </KeylessPageLayout>
    );
  }

  return <Loading active={isLoading} />;
}

const styles = {
  page: {
    height: "100dvh",
    paddingBottom: "60px",
    fontFamily: "'BlissLight'",
  },
  pageContent: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    gap: "1rem",
  },
  filler: {
    flex: 1,
  },
  hint: {
    display: "flex",
    alignItems: 'flex-start',
    gap: '.5rem',
  },
  hintIcon: {
    width: '16px',
  },
  title: {
    color: keylessColors.green,
    textAlign: "center",
    fontSize: "26px",
  },
  vehicleImage: {
    height: "13vh",
    margin: "0 auto",
    display: "block",
  },
  parkingImage: {
    height: "20vh",
    margin: "0 auto",
    display: "block",
  },
  plateNumber: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    color: "white",
    backgroundColor: keylessColors.green,
    borderRadius: "5px",
    padding: "4px 16px",
    margin: "0 auto",
    width: "max-content",
    fontSize: "20px",
  },
  brand: {
    textAlign: "center",
    fontSize: "16px",
    marginTop: "1rem",
  },
  details: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
  },
  detailsItem: {
    fontSize: "14px",
    display: "grid",
    justifyItems: "center",
  },
  detailsItemImgWrapper: {
    width: "60px",
    height: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  detailsItemImg: {
    height: "60%",
  },
  parkingLocation: {
    backgroundColor: keylessColors.green,
    borderRadius: "20px",
    padding: "5px",
    width: "70%",
    color: "white",
    fontSize: "20px",
    margin: "0 auto",
    textAlign: "center",
  },
  parkingLocationValue: {
    fontSize: "20px",
  },
  qrcodeInputImg: {
    height: "20vh",
    margin: "0 auto",
    display: "block",
  },
  qrcodeInputWrapper: {
    position: "relative",
    width: "100%",
  },
  qrcodeInput: {
    opacity: "0",
    position: "absolute",
    width: "100%",
    height: "100%",
    top: "0",
    left: "0",
  },
};
